import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import numbro from 'numbro';

import {
  CurrencyType,
  getCurrencyDetails,
} from '../../../constants/currencies';
import { SVGIconType } from '../../../constants/svgIcon';
import { BalanceType } from '../../../models/Base';
import { BreadBar, CountDownText, PngIcon, SvgIcon } from '../../Molecules';
import { BasicPopupContainer } from '../Containers/BasicPopupContainer';

import styles from './CurrencyDetailPopup.module.css';

export type CurrencyDetailPopupProps = {
  currencyType: CurrencyType;
  currentAmount: number;
  totalAmount: number | undefined;
  willRefillAt: Date | undefined;
  isTrialUser: boolean | undefined;
  onPrimary: (() => void) | undefined;
  onCancel: () => void;
};

export const CurrencyDetailPopup = memo(
  ({
    currencyType,
    currentAmount,
    totalAmount,
    willRefillAt,
    isTrialUser,
    onPrimary,
    onCancel,
  }: CurrencyDetailPopupProps) => {
    const { t } = useTranslation();
    const currencyDetails = getCurrencyDetails(currencyType);

    let primaryButtonLabel = 'Get More';
    if (onPrimary === undefined) {
      primaryButtonLabel = 'OK';
    } else if (isTrialUser) {
      primaryButtonLabel = 'Register';
    }

    return (
      <BasicPopupContainer
        primaryButtonLabel={t(primaryButtonLabel)}
        primaryButtonIcon={
          primaryButtonLabel === 'Register' ? (
            <SvgIcon name={SVGIconType.Register} width={24} height={24} />
          ) : undefined
        }
        secondaryButtonLabel={t('OK')}
        onPrimaryButtonClick={onPrimary ?? onCancel}
        onSecondaryButtonClick={onPrimary ? onCancel : undefined}
      >
        <div className={styles.popupContent}>
          <div className={styles.noticeIcon}>
            <PngIcon name={currencyDetails.icon} width={80} height={80} />
          </div>
          <div className={styles.title}>
            {t(currencyDetails.fullyDisplayName)}
          </div>
          <div className={styles.staminaInfo}>
            <p className={styles.youHaveLabel}>{t('You have:')}</p>
            <div className={styles.staminaDetails}>
              {currencyType === BalanceType.BreadSlice ? (
                <BreadBar
                  breadSlice={currentAmount}
                  width="160px"
                  iconSize={34}
                  backgroundColor="#fff"
                />
              ) : (
                <>
                  <PngIcon name={currencyDetails.icon} width={24} height={24} />
                  {totalAmount ? (
                    <span>{`${currentAmount} / ${totalAmount}`}</span>
                  ) : (
                    <span>
                      {numbro(currentAmount).format({
                        thousandSeparated: true,
                      })}
                    </span>
                  )}
                </>
              )}
            </div>
            {!isTrialUser && willRefillAt ? (
              <div className={styles.refillTextBar}>
                <SvgIcon
                  name={SVGIconType.ClockRefill}
                  className={styles.timerIcon}
                />
                <CountDownText
                  timestamp={willRefillAt.getTime()}
                  label={t('Next Refill: ')}
                  className={styles.staminaText}
                />
              </div>
            ) : null}
          </div>
          {isTrialUser && totalAmount !== undefined ? (
            <div className={styles.registerText}>
              {t(currencyDetails.registerText)}
            </div>
          ) : (
            <div className={styles.description}>
              {t(currencyDetails.description)}
            </div>
          )}
        </div>
      </BasicPopupContainer>
    );
  },
);
